import { motion } from 'framer-motion'
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { useState, useEffect } from 'react'

// Data for spending trend
const spendingData = [
  { month: 'Jan', amount: 2400, savings: 400 },
  { month: 'Feb', amount: 1800, savings: 800 },
  { month: 'Mar', amount: 2200, savings: 600 },
  { month: 'Apr', amount: 1600, savings: 900 },
  { month: 'Mai', amount: 1900, savings: 850 },
  { month: 'Jun', amount: 1400, savings: 1100 },
]

// Data for category breakdown
const categoryData = [
  { name: 'Essen', value: 450, color: '#3b82f6' },
  { name: 'Transport', value: 280, color: '#8b5cf6' },
  { name: 'Wohnen', value: 890, color: '#ec4899' },
  { name: 'Freizeit', value: 320, color: '#10b981' },
  { name: 'Sonstiges', value: 180, color: '#f59e0b' },
]

// Data for income vs expenses
const cashflowData = [
  { month: 'Jan', income: 3200, expenses: 2400 },
  { month: 'Feb', income: 3200, expenses: 1800 },
  { month: 'Mar', income: 3500, expenses: 2200 },
  { month: 'Apr', income: 3200, expenses: 1600 },
  { month: 'Mai', income: 3400, expenses: 1900 },
  { month: 'Jun', income: 3200, expenses: 1400 },
]

export const AnimatedLineChart = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="w-full h-full"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={spendingData}>
          <defs>
            <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="month" 
            stroke="#9ca3af" 
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="#9ca3af" 
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1f2937', 
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
            labelStyle={{ color: '#fff' }}
            itemStyle={{ color: '#fff' }}
          />
          <Line 
            type="monotone" 
            dataKey="amount" 
            stroke="#3b82f6" 
            strokeWidth={3}
            dot={{ fill: '#3b82f6', r: 5 }}
            activeDot={{ r: 8, fill: '#3b82f6', stroke: '#3b82f6' }}
            animationDuration={1500}
            name="Ausgaben"
          />
          <Line 
            type="monotone" 
            dataKey="savings" 
            stroke="#10b981" 
            strokeWidth={3}
            dot={{ fill: '#10b981', r: 5 }}
            activeDot={{ r: 8, fill: '#10b981', stroke: '#10b981' }}
            animationDuration={1500}
            name="Ersparnis"
          />
        </LineChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

export const AnimatedAreaChart = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="w-full h-full"
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={cashflowData}>
          <defs>
            <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="month" 
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1f2937', 
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
            labelStyle={{ color: '#fff' }}
            itemStyle={{ color: '#fff' }}
          />
          <Area 
            type="monotone" 
            dataKey="income" 
            stroke="#10b981" 
            strokeWidth={2}
            fillOpacity={1} 
            fill="url(#colorIncome)"
            animationDuration={1500}
            name="Einnahmen"
          />
          <Area 
            type="monotone" 
            dataKey="expenses" 
            stroke="#ef4444" 
            strokeWidth={2}
            fillOpacity={1} 
            fill="url(#colorExpenses)"
            animationDuration={1500}
            name="Ausgaben"
          />
        </AreaChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

export const AnimatedPieChart = () => {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % categoryData.length)
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0, rotate: -10 }}
      whileInView={{ opacity: 1, rotate: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="w-full h-full flex items-center justify-center"
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={categoryData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={60}
            paddingAngle={5}
            dataKey="value"
            animationBegin={0}
            animationDuration={1000}
            label={(entry) => entry.name}
            labelLine={false}
          >
            {categoryData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color}
                opacity={index === activeIndex ? 1 : 0.7}
                style={{
                  filter: index === activeIndex ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : 'none',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1f2937', 
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
            labelStyle={{ color: '#fff' }}
            itemStyle={{ color: '#fff' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

export const AnimatedBarChart = () => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="w-full h-full"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={spendingData}>
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={1}/>
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="month" 
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1f2937', 
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
            labelStyle={{ color: '#fff' }}
            itemStyle={{ color: '#fff' }}
          />
          <Bar 
            dataKey="amount" 
            fill="url(#barGradient)" 
            radius={[10, 10, 0, 0]}
            animationDuration={1500}
            name="Ausgaben"
            activeBar={{ fill: 'url(#barGradient)', opacity: 0.8 }}
          />
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

// Compact dashboard showing multiple metrics
export const DashboardPreview = () => {
  const metrics = [
    { label: 'Gespart diesen Monat', value: '€1,100', trend: '+12%', color: 'text-green-400' },
    { label: 'Ausgaben reduziert', value: '€420', trend: '-18%', color: 'text-blue-400' },
    { label: 'Sparziel Fortschritt', value: '73%', trend: '+5%', color: 'text-purple-400' },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="w-full space-y-4"
    >
      {metrics.map((metric, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 hover:border-blue-500/50 transition-all"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 mb-1">{metric.label}</p>
              <p className={`text-2xl font-bold ${metric.color}`}>{metric.value}</p>
            </div>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`text-lg font-semibold ${metric.color}`}
            >
              {metric.trend}
            </motion.div>
          </div>
        </motion.div>
      ))}
      
      <AnimatedAreaChart />
    </motion.div>
  )
}
